<script setup lang="ts">
import { ref, computed } from "vue";
import { useTranslation } from "i18next-vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "@/components/UI/DRDropdownItem.vue";
import DREditTextDialog from "@/components/UI/feedbacks/DREditTextDialog.vue";
import DRBranchInfoDialog from "./DRBranchInfoDialog.vue";
import {
  useSwitchDrawingBranch,
  useRenameBranch,
  useCreateEventLog,
} from "@/stores/queries";
import { EventType, type DRBranchWithContributors } from "@/types";
import type { RulesProps } from "@/composable/useValidation";
import { useDownloadDwg } from "@/components/files/DrawingList/downloadFile";
import { useDeviceState } from "@/stores/DeviceState";

const props = defineProps<{
  drawingId: number;
  drawingName: string;
  branch: DRBranchWithContributors;
  isCurrent: boolean;
}>();

const emits = defineEmits<{
  (e: "switched-branch"): void;
}>();

const { createEvent } = useCreateEventLog();
const { mutateAsync: downloadDwg, isLoading: isDownloading } = useDownloadDwg();
const deviceState = useDeviceState();

const { t } = useTranslation();
const { mutate: switchBranch, isPending: isSwitching } =
  useSwitchDrawingBranch();
const { mutate: renameBranch, isPending: isRenaming } = useRenameBranch();

const isRenameDialogOpen = ref(false);
const isInfoDialogOpen = ref(false);

const rules = computed<RulesProps[]>(() => [
  {
    validator: (value: string) => !!value.trim()?.length,
    message: t("common:validation.required.name"),
  },
]);

const handleSwitchBranch = () => {
  if (props.isCurrent) return;
  switchBranch({
    drawingId: props.drawingId,
    branchId: props.branch.id,
  });
  emits("switched-branch");
};

const handleRenameBranch = (newName: string) => {
  renameBranch({
    drawingId: props.drawingId,
    branchId: props.branch.id,
    name: newName,
  });
};

const openInfoDialog = () => {
  isInfoDialogOpen.value = true;
  createEvent(EventType.DRAWING_BRANCH_ABOUT);
};

const handleDownload = () => {
  if (!props.branch.headRevisionId) return;
  downloadDwg(props.drawingName, props.drawingId, props.branch.headRevisionId);
};
</script>

<template>
  <el-dropdown trigger="click" @click.stop.prevent>
    <DRBtnIcon
      icon="bi-three-dots"
      variant="flat"
      :size="30"
      @click.stop.prevent
      @dblclick.stop.prevent
    />

    <template #dropdown>
      <el-dropdown-menu>
        <DRDropdownItem
          @select="handleSwitchBranch()"
          :disabled="!!isCurrent || isSwitching"
          icon="bi-diagram-2"
          :label="$t('dashboard:branches.switchTo')"
        />
        <DRDropdownItem
          @select="isRenameDialogOpen = true"
          :disabled="isRenaming"
          icon="bi-pencil"
          :label="$t('dashboard:branches.rename')"
        />
        <DRDropdownItem
          @select="handleDownload"
          :disabled="!branch.headRevisionId || !deviceState.isOnline"
          :loading="isDownloading"
          icon="bi-file-earmark-arrow-down"
          :label="$t('drawings.download.dwg')"
        />
        <DRDropdownItem
          @select="openInfoDialog"
          icon="bi-info-circle"
          :label="$t('dashboard:branches.about')"
        />
      </el-dropdown-menu>
    </template>
  </el-dropdown>

  <DREditTextDialog
    v-model="isRenameDialogOpen"
    :title="$t('dashboard:branches.renameDialog.title')"
    :label="$t('dashboard:branches.renameDialog.label')"
    :value="branch.name"
    :placeholder="$t('dashboard:branches.renameDialog.placeholder')"
    :rules="rules"
    @confirm="handleRenameBranch($event)"
  />

  <DRBranchInfoDialog v-model="isInfoDialogOpen" :branch="branch" />
</template>

<style lang="scss" scoped></style>
